import { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CloudUpload } from "lucide-react";
import { useCloudTargets, sendToCloud, cloudName } from "./SendToCloud";

// -------------------- Image thumbnail --------------------
// Lazy-loads the actual file bytes as a blob URL so image cards show a real
// preview instead of a generic icon. Cache is process-lifetime (in-memory) so
// each file is fetched at most once per session.
const THUMB_CACHE = new Map(); // file_id -> objectURL
const FileThumb = ({ fileId, fallback }) => {
  const [src, setSrc] = useState(() => THUMB_CACHE.get(fileId) || null);
  useEffect(() => {
    if (THUMB_CACHE.has(fileId)) { setSrc(THUMB_CACHE.get(fileId)); return undefined; }
    let cancel = false;
    api.get(`/files/${fileId}/download`, { responseType: "blob" })
      .then((r) => {
        if (cancel) return;
        const url = URL.createObjectURL(r.data);
        THUMB_CACHE.set(fileId, url);
        setSrc(url);
      })
      .catch(() => { /* fall back to icon */ });
    return () => { cancel = true; };
  }, [fileId]);
  if (!src) return fallback;
  return <img src={src} alt="" className="mb-3 h-24 w-full rounded object-cover" loading="lazy" />;
};
import {
  Folder,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  Upload,
  FolderPlus,
  LayoutGrid,
  List as ListIcon,
  MoreVertical,
  Download,
  Pencil,
  Trash2,
  ChevronRight,
  Search,
  HardDrive,
  AlertTriangle,
  Home,
  Mail,
  Copy as CopyIcon,
  Move as MoveIcon,
  CheckSquare,
  Square,
  Globe,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import FilePreview from "@/components/dashboard/FilePreview";
import FolderPicker from "@/components/dashboard/FolderPicker";
import { useCompose } from "@/hooks/useCompose";
import { BrowserAccessContext } from "@/components/dashboard/browser/OpenInBrowser";
import { FileContextMenu, useFileContextMenu } from "@/components/dashboard/files/FileContextMenu";

const fmtSize = (b) => {
  if (!b) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const iconFor = (item) => {
  if (item.is_folder) return { Icon: Folder, cls: "text-[#ffb100] fill-[#ffb100]/20" };
  const m = (item.mime_type || "").toLowerCase();
  const n = (item.name || "").toLowerCase();
  if (m.startsWith("image/")) return { Icon: ImageIcon, cls: "text-emerald-500" };
  if (m.includes("pdf") || n.endsWith(".pdf")) return { Icon: FileText, cls: "text-red-500" };
  if (m.includes("sheet") || n.match(/\.(xlsx?|csv)$/)) return { Icon: FileSpreadsheet, cls: "text-green-600" };
  if (m.includes("word") || n.match(/\.docx?$/)) return { Icon: FileText, cls: "text-blue-600" };
  return { Icon: FileIcon, cls: "text-gray-400" };
};

import { ChevronDown as CaretDown, ChevronRight as CaretRight } from "lucide-react";

// -------------------- Sidebar folder tree --------------------
// Windows-Explorer-style tree. Renders every folder as a nested list; each
// row is a click-to-open link and a chevron toggles its children.
const FolderTree = ({ folders, currentId, expanded, onToggle, onOpen, parentId = "root", depth = 0 }) => {
  const children = folders.filter((f) => (f.parent_id || "root") === parentId);
  if (children.length === 0) return null;
  return (
    <ul className={depth === 0 ? "space-y-0.5" : "ml-4 space-y-0.5 border-l border-gray-200 pl-2"}>
      {children.map((f) => {
        const isOpen = expanded.has(f.file_id);
        const hasKids = folders.some((x) => (x.parent_id || "root") === f.file_id);
        const isCurrent = currentId === f.file_id;
        return (
          <li key={f.file_id}>
            <div className={`group flex items-center gap-1 rounded px-1 py-1 text-[13px] hover:bg-gray-100 ${isCurrent ? "bg-[#eef4fb] text-[#0364b8]" : "text-gray-700"}`}>
              <button
                type="button"
                onClick={() => onToggle(f.file_id)}
                className={`flex h-4 w-4 items-center justify-center text-gray-400 ${hasKids ? "" : "invisible"}`}
                aria-label={isOpen ? "Collapse" : "Expand"}
              >
                {isOpen ? <CaretDown className="h-3.5 w-3.5" /> : <CaretRight className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                onClick={() => onOpen(f.file_id)}
                data-testid={`folder-tree-${f.file_id}`}
                className="flex flex-1 items-center gap-1.5 truncate text-left"
              >
                <Folder className="h-3.5 w-3.5 shrink-0 text-[#ffb100] fill-[#ffb100]/20" />
                <span className="truncate">{f.name}</span>
              </button>
            </div>
            {/* NOTE: called as a plain function, not <FolderTree /> — the
                dev-server visual-edits babel plugin infinitely recurses on
                self-referencing JSX components and crashes the build. */}
            {isOpen &&
              FolderTree({
                folders,
                currentId,
                expanded,
                onToggle,
                onOpen,
                parentId: f.file_id,
                depth: depth + 1,
              })}
          </li>
        );
      })}
    </ul>
  );
};

// -------------------- Windows-Explorer-style copy progress --------------------
const fmtBytes = (n) => {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};
const fmtEta = (sec) => {
  if (!Number.isFinite(sec)) return "estimating…";
  if (sec < 60) return `${Math.max(1, Math.round(sec))} sec`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m < 60) return `${m} min ${s} sec`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h} hr ${mm} min`;
};
const CopyProgressModal = ({ progress, onCancel }) => {
  if (!progress) return null;
  const pct = progress.totalBytes > 0 ? Math.min(100, (progress.doneBytes / progress.totalBytes) * 100) : 0;
  const remainingItems = Math.max(0, (progress.totalItems || 0) - (progress.done || 0));
  const remainingBytes = Math.max(0, (progress.totalBytes || 0) - (progress.doneBytes || 0));
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" data-testid="copy-progress-modal">
      <div className="w-full max-w-md overflow-hidden rounded-md bg-white shadow-2xl">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-800">
          {pct >= 100 ? "Copy complete" : `Copying ${progress.totalItems} item${progress.totalItems === 1 ? "" : "s"}`}
        </div>
        <div className="space-y-3 px-5 py-4 text-sm text-gray-700">
          <div className="truncate">
            <span className="text-gray-500">From:</span> <span className="font-medium">{progress.currentPath || progress.currentName}</span>
          </div>
          <div className="truncate">
            <span className="text-gray-500">To:</span> <span className="font-medium">{progress.destName}</span>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between font-mono text-xs">
              <span>{Math.round(pct)}% complete</span>
              <span className="text-gray-500">{fmtBytes(progress.speed)}/sec</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
              <div className="h-full rounded-full bg-[#0364b8] transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            <div><span className="text-gray-500">Time remaining:</span> {fmtEta(progress.eta)}</div>
            <div><span className="text-gray-500">Items remaining:</span> {remainingItems.toLocaleString()} ({fmtBytes(remainingBytes)})</div>
            <div className="col-span-2 truncate"><span className="text-gray-500">Name:</span> <span className="font-mono">{progress.currentName || "—"}</span></div>
          </div>
          {progress.failures && progress.failures.length > 0 && (
            <div className="max-h-32 overflow-y-auto rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              <p className="mb-1 font-semibold">{progress.failures.length} failed</p>
              {progress.failures.slice(0, 5).map((f, i) => <p key={i} className="truncate">{f}</p>)}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            data-testid="copy-progress-cancel"
          >
            {pct >= 100 ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FilesTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFolder = searchParams.get("folder") || "root";
  const [parent, setParentState] = useState(urlFolder);

  // Navigate — pushes a history entry so the browser Back button walks the
  // folder trail before leaving the Files tab.
  const setParent = useCallback((pid) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", "files");
        if (pid && pid !== "root") p.set("folder", pid);
        else p.delete("folder");
        return p;
      },
      { replace: false }
    );
  }, [setSearchParams]);
  const [items, setItems] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [query, setQuery] = useState("");
  const [usage, setUsage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const contentScrollRef = useRef(null);
  const rootRef = useRef(null);
  const [rootHeight, setRootHeight] = useState(0);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null); // single-item delete confirm
  const browserOn = useContext(BrowserAccessContext);
  const { ctx, setCtx, handlersFor } = useFileContextMenu();
  const [renameValue, setRenameValue] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState(null); // "move" | "copy"
  const [bulkBusy, setBulkBusy] = useState(false);
  const { openCompose } = useCompose();
  const fileRef = useRef(null);
  // Windows-Explorer-style copy progress modal state
  const [copyProgress, setCopyProgress] = useState(null); // {done, total, doneBytes, totalBytes, startTs, speed, currentName, currentPath, destName, cancelled, failures}
  const copyCancelRef = useRef(false);
  // Full folder tree for the left-sidebar tree navigator
  const [folders, setFolders] = useState([]);
  const [expandedTree, setExpandedTree] = useState(() => new Set(["root"]));

  const load = useCallback(async (pid = parent) => {
    setLoading(true);
    try {
      const [r, u, tree] = await Promise.all([
        api.get("/files", { params: { parent: pid } }),
        api.get("/files/usage"),
        api.get("/files/folders"),
      ]);
      setItems(r.data.items || []);
      setBreadcrumb(r.data.breadcrumb || []);
      setUsage(u.data);
      setFolders(tree.data?.folders || []);
      // Auto-expand ancestors of the current parent so the tree opens to it.
      if (pid && pid !== "root") {
        const byId = new Map((tree.data?.folders || []).map((f) => [f.file_id, f]));
        const ancestors = new Set(["root"]);
        let cur = byId.get(pid);
        let depth = 0;
        while (cur && depth < 20) {
          ancestors.add(cur.file_id);
          cur = cur.parent_id && cur.parent_id !== "root" ? byId.get(cur.parent_id) : null;
          depth += 1;
        }
        setExpandedTree((s) => new Set([...s, ...ancestors]));
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [parent]);

  useEffect(() => {
    load(parent);
  }, [parent, load]);

  // Sync URL folder param → local state so browser back/forward walks folders.
  useEffect(() => {
    if (urlFolder !== parent) setParentState(urlFolder);
  }, [urlFolder, parent]);

  // Reset right-pane scroll to the top whenever we navigate into a different
  // folder — the left sidebar keeps its own scroll position (Windows Explorer
  // behaviour).
  useEffect(() => {
    if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0;
  }, [parent]);

  // Pin the root container to the visible viewport so the two panes scroll
  // independently instead of the whole page scrolling. We compute the height
  // dynamically because the top nav + tab bar + icon strip above us varies in
  // height on different screens.
  useEffect(() => {
    const measure = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top;
      setRootHeight(Math.max(300, window.innerHeight - top));
    };
    measure();
    window.addEventListener("resize", measure);
    // Some layout shifts (image loads, deferred paints) happen after mount —
    // remeasure a few times in the first second to catch them.
    const t1 = setTimeout(measure, 100);
    const t2 = setTimeout(measure, 400);
    const t3 = setTimeout(measure, 1000);
    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
    };
  }, []);

  const openItem = (item) => {
    if (item.is_folder) setParent(item.file_id);
    else setPreviewFile(item);
  };

  // Google Drive Streaming exposes documents as tiny placeholder stubs
  // (.gsheet, .gdoc, .gslides, .gform, .gsite, .glink). Chrome can't reliably
  // read their bytes without a live Google Drive fetch — the upload times out
  // with a bogus "Network Error". Detect and skip them with a clear reason.
  const GOOGLE_STUB_EXT = /\.(gsheet|gdoc|gslides|gform|gsite|gdraw|glink|gmap|gnote|gtable)$/i;
  const isGoogleStub = (f) => GOOGLE_STUB_EXT.test(f?.name || "");

  // Upload each file in its OWN request so axios `onUploadProgress` can drive
  // a live Windows-Explorer-style progress modal (percent, MB/s, ETA, items
  // remaining). No client-side batch size cap.
  const uploadOne = async (file, parentId, onBytes) => {
    const fd = new FormData();
    fd.append("parent_id", parentId);
    fd.append("files", file, file.name);
    return api.post("/files/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (typeof evt.loaded === "number") onBytes(evt.loaded);
      },
    });
  };

  const runCopy = async (jobs, destName) => {
    // Split out Google Drive Streaming stubs upfront — they'll all error out
    // and just spam the failure list.
    const skipped = jobs.filter((j) => isGoogleStub(j.file));
    const realJobs = jobs.filter((j) => !isGoogleStub(j.file));
    if (!realJobs.length) {
      if (skipped.length) {
        toast.warning(`Skipped ${skipped.length} Google Drive shortcut${skipped.length === 1 ? "" : "s"} — those live in Google Drive, not on disk.`);
      }
      return { uploaded: 0, failures: [] };
    }
    const totalBytes = realJobs.reduce((s, j) => s + j.file.size, 0);
    const startTs = performance.now();
    let doneBytes = 0;
    let bytesInCurrent = 0;
    copyCancelRef.current = false;
    const setPct = (extraBytes = 0) => {
      const cur = doneBytes + extraBytes;
      const elapsed = (performance.now() - startTs) / 1000; // s
      const speed = elapsed > 0 ? cur / elapsed : 0; // B/s
      const remaining = Math.max(0, totalBytes - cur);
      const eta = speed > 0 ? remaining / speed : Infinity;
      setCopyProgress((p) => ({
      ...(p || {}),
      totalBytes,
      doneBytes: cur,
      totalItems: realJobs.length,
      speed,
      eta,
      destName,
    }));
    };
    setCopyProgress({
      done: 0, totalItems: realJobs.length,
      doneBytes: 0, totalBytes,
      speed: 0, eta: Infinity,
      currentName: realJobs[0].file.name, currentPath: realJobs[0].displayPath,
      destName,
      failures: [],
    });
    const failures = [];
    let uploaded = 0;
    let processed = 0;
    for (let i = 0; i < realJobs.length; i += 1) {
      if (copyCancelRef.current) break;
      const j = realJobs[i];
      setCopyProgress((p) => ({ ...(p || {}), done: processed, currentName: j.file.name, currentPath: j.displayPath }));
      bytesInCurrent = 0;
      try {
        await uploadOne(j.file, j.parentId, (loaded) => { bytesInCurrent = loaded; setPct(bytesInCurrent); });
        doneBytes += j.file.size;
        uploaded += 1;
      } catch (e) {
        failures.push(`${j.file.name}: ${e?.response?.data?.detail || e?.message || "network"}`);
        doneBytes += j.file.size; // count as processed so ETA doesn't stall
      }
      processed += 1;
      setPct(0);
    }
    if (skipped.length) failures.push(`Skipped ${skipped.length} Google Drive shortcut${skipped.length === 1 ? "" : "s"}`);
    setCopyProgress((p) => ({ ...(p || {}), done: processed, failures }));
    return { uploaded, failures };
  };

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (files.length === 0) {
      toast.warning("Nothing to upload (empty files skipped).");
      return;
    }
    setUploading(true);
    const jobs = files.map((f) => ({ file: f, parentId: parent, displayPath: f.name }));
    const destName = breadcrumb[breadcrumb.length - 1]?.name || "My files";
    const { uploaded, failures } = await runCopy(jobs, destName);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    // Auto-close the progress modal shortly after success; keep it open on failure
    // so the user can see which files errored.
    if (failures.length === 0) {
      toast.success(`Copied ${uploaded} item${uploaded === 1 ? "" : "s"}`);
      setTimeout(() => setCopyProgress(null), 1200);
    } else {
      toast.warning(`Copied ${uploaded} of ${files.length}. ${failures.length} failed.`);
    }
    await load(parent);
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await api.post("/files/folder", { name, parent_id: parent });
      setNewFolderOpen(false);
      setNewFolderName("");
      await load(parent);
    } catch {
      toast.error("Could not create folder");
    }
  };

  const download = async (item) => {
    try {
      const res = await api.get(`/files/${item.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const doRename = async () => {
    if (!renameValue.trim()) return;
    try {
      await api.put(`/files/${renameTarget.file_id}`, { name: renameValue.trim() });
      setRenameTarget(null);
      await load(parent);
    } catch {
      toast.error("Rename failed");
    }
  };

  const remove = (item) => setConfirmItem(item);
  const doRemove = async () => {
    const item = confirmItem;
    setConfirmItem(null);
    try {
      await api.delete(`/files/${item.file_id}`);
      setItems((prev) => prev.filter((x) => x.file_id !== item.file_id));
      toast.success(`Deleted ${item.name}`);
      load(parent);
    } catch {
      toast.error("Delete failed");
    }
  };

  // Pin a file on the in-app browser's files bar for one-click uploads into websites.
  const addToBrowser = async (item) => {
    try {
      await api.post("/browser/files", { file_id: item.file_id });
      toast.success(`${item.name} is on the browser's files bar`, { description: "Open a site's upload button and pick it from the bar." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to browser");
    }
  };

  // Recursively walk a DataTransferItem entry tree so drag-dropped folders
  // (with subfolders) get flattened to `{path: string[], file: File}`.
  const readDropTree = (entry, prefix = []) => new Promise((resolve) => {
    if (!entry) return resolve([]);
    if (entry.isFile) {
      entry.file(
        (file) => resolve([{ path: prefix, file }]),
        () => resolve([]),
      );
      return;
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const all = [];
      const readBatch = () => reader.readEntries(async (entries) => {
        if (entries.length === 0) return resolve(all);
        for (const child of entries) {
          const found = await readDropTree(child, [...prefix, entry.name]);
          all.push(...found);
        }
        readBatch();
      }, () => resolve(all));
      readBatch();
      return;
    }
    resolve([]);
  });

  // Upload the flattened tree: create every needed subfolder (in BFS order)
  // then upload every file — one request per file — driving the Windows
  // Explorer-style copy progress modal.
  const doUploadTree = async (entries) => {
    if (!entries.length) return;
    setUploading(true);
    const uniquePaths = Array.from(new Set(entries.map((e) => e.path.join("/")).filter(Boolean)))
      .sort((a, b) => a.split("/").length - b.split("/").length);
    const pathToId = { "": parent };
    let createdFolders = 0;
    setCopyProgress({
      done: 0, totalItems: entries.length, doneBytes: 0,
      totalBytes: entries.reduce((s, e) => s + (e.file?.size || 0), 0),
      speed: 0, eta: Infinity,
      currentName: "Creating folders…", currentPath: uniquePaths[0] || "",
      destName: breadcrumb[breadcrumb.length - 1]?.name || "My files",
      failures: [],
    });
    try {
      for (const p of uniquePaths) {
        const segs = p.split("/");
        const parentPath = segs.slice(0, -1).join("/");
        const parentId = pathToId[parentPath] ?? parent;
        const r = await api.post("/files/folder", { name: segs[segs.length - 1], parent_id: parentId });
        pathToId[p] = r.data.file_id;
        createdFolders += 1;
        setCopyProgress((cp) => ({ ...(cp || {}), currentName: `Creating folder ${segs[segs.length - 1]}`, currentPath: p }));
      }
    } catch (e) {
      setUploading(false);
      setCopyProgress(null);
      toast.error(`Folder creation failed: ${e?.response?.data?.detail || e.message || "unknown"}`);
      await load(parent);
      return;
    }
    const rootName = uniquePaths[0]?.split("/")[0] || "";
    const jobs = entries.map(({ path, file }) => ({
      file,
      parentId: pathToId[path.join("/")] ?? parent,
      displayPath: path.length ? `${path.join("/")}/${file.name}` : file.name,
    }));
    const destName = breadcrumb[breadcrumb.length - 1]?.name || "My files";
    const { uploaded, failures } = await runCopy(jobs, destName);
    setUploading(false);
    if (failures.length === 0) {
      toast.success(`Copied ${uploaded} item${uploaded === 1 ? "" : "s"}${rootName ? ` from ${rootName}` : ""} to ${destName}`);
      setTimeout(() => setCopyProgress(null), 1200);
    } else {
      toast.warning(`Copied ${uploaded} of ${entries.length}, ${failures.length} failed`);
    }
    await load(parent);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    // Snapshot everything synchronously — DataTransfer is invalidated after
    // the drop handler yields (returns from `await`), so we grab both
    // `items` (needed for folder traversal via webkitGetAsEntry) and the
    // flat `files` list up front.
    const itemsArr = Array.from(e.dataTransfer?.items || []);
    const flatFiles = Array.from(e.dataTransfer?.files || []);
    // Pull directory/file entries synchronously — this must happen inside
    // the event handler, not after await, otherwise Chrome invalidates them.
    const rootEntries = itemsArr
      .map((it) => (typeof it.webkitGetAsEntry === "function" ? it.webkitGetAsEntry() : null))
      .filter(Boolean);

    if (rootEntries.length > 0) {
      try {
        const results = await Promise.all(rootEntries.map((en) => readDropTree(en, [])));
        const entries = results.flat().filter((x) => x && x.file);
        const anyDir = entries.some((x) => x.path.length > 0);
        if (anyDir) return doUploadTree(entries);
        if (entries.length) return doUpload(entries.map((x) => x.file));
      } catch (err) {
        toast.error(`Drop failed: ${err?.message || "traversal error"}`);
        return;
      }
    }
    if (flatFiles.length) doUpload(flatFiles);
  };

  const filtered = items.filter((i) => !query || i.name.toLowerCase().includes(query.toLowerCase()));
  const pct = usage ? Math.min(100, (usage.used_bytes / usage.quota_bytes) * 100) : 0;
  const diskFreePct = usage?.disk_total_bytes ? (usage.disk_free_bytes / usage.disk_total_bytes) * 100 : null;
  const lowDisk = diskFreePct != null && diskFreePct < 10;

  // ---------- bulk selection ----------
  const selectableIds = useMemo(() => filtered.map((i) => i.file_id), [filtered]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const selectedItems = filtered.filter((i) => selected.has(i.file_id));
  const selectedFileIds = selectedItems.filter((i) => !i.is_folder).map((i) => i.file_id);

  // Clear selection whenever the folder changes or the list reloads with new ids.
  useEffect(() => { setSelected(new Set()); }, [parent]);

  const toggleOne = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selected)) {
      try { await api.delete(`/files/${id}`); ok += 1; } catch { fail += 1; }
    }
    setBulkBusy(false);
    setConfirmDeleteOpen(false);
    clearSelection();
    if (ok) toast.success(`Deleted ${ok} item${ok === 1 ? "" : "s"}`);
    if (fail) toast.error(`Could not delete ${fail} item${fail === 1 ? "" : "s"}`);
    await load(parent);
  };

  const bulkMove = async (destParentId) => {
    let ok = 0, fail = 0;
    for (const id of Array.from(selected)) {
      try { await api.put(`/files/${id}/move`, { parent_id: destParentId }); ok += 1; } catch { fail += 1; }
    }
    setPickerMode(null);
    clearSelection();
    if (ok) toast.success(`Moved ${ok} item${ok === 1 ? "" : "s"}`);
    if (fail) toast.error(`Could not move ${fail} item${fail === 1 ? "" : "s"}`);
    await load(parent);
  };

  const bulkCopy = async (destParentId) => {
    let ok = 0, fail = 0;
    for (const id of Array.from(selected)) {
      const item = filtered.find((x) => x.file_id === id);
      if (!item || item.is_folder) { fail += 1; continue; }
      try { await api.post(`/files/${id}/copy`, { parent_id: destParentId }); ok += 1; } catch { fail += 1; }
    }
    setPickerMode(null);
    clearSelection();
    if (ok) toast.success(`Copied ${ok} file${ok === 1 ? "" : "s"}`);
    if (fail) toast.error(`Could not copy ${fail} item${fail === 1 ? "" : "s"}`);
    await load(parent);
  };

  const bulkEmail = async () => {
    if (selectedFileIds.length === 0) {
      toast.error("Select at least one file (folders can't be attached)");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/files/attachments", { file_ids: selectedFileIds });
      openCompose({ files: r.data?.attachments || [] });
      clearSelection();
    } catch {
      toast.error("Could not attach files");
    } finally {
      setBulkBusy(false);
    }
  };

  const cloudTargets = useCloudTargets();
  const ItemActions = ({ item }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="file-actions-btn" aria-label="Actions" className="rounded p-1.5 text-gray-500 hover:bg-gray-200" onClick={(e) => e.stopPropagation()}>
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40 bg-white text-gray-700">
        {!item.is_folder && (
          <DropdownMenuItem onClick={() => download(item)} data-testid="file-download">
            <Download className="mr-2 h-4 w-4" /> Download
          </DropdownMenuItem>
        )}
        {!item.is_folder && cloudTargets.map((t) => (
          <DropdownMenuItem key={t.provider} onClick={() => sendToCloud(t.provider, { name: item.name, apiPath: `/api/files/${item.file_id}/download`, category: "Local files" })} data-testid={`file-send-${t.provider}`}>
            <CloudUpload className="mr-2 h-4 w-4" /> Send to {cloudName(t.provider)}
          </DropdownMenuItem>
        ))}
        {!item.is_folder && browserOn && (
          <DropdownMenuItem onClick={() => addToBrowser(item)} data-testid="file-add-browser">
            <Globe className="mr-2 h-4 w-4" /> Add to browser
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => { setRenameTarget(item); setRenameValue(item.name); }} data-testid="file-rename">
          <Pencil className="mr-2 h-4 w-4" /> Rename
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => remove(item)} data-testid="file-delete" className="text-red-600 focus:text-red-600">
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div
      ref={rootRef}
      style={rootHeight ? { height: `${rootHeight}px` } : undefined}
      className="flex min-h-0 overflow-hidden bg-[#faf9f8] text-gray-800"
      data-testid="files-tab"
    >
      {/* sidebar (scrolls independently of the right pane) */}
      <aside className="hidden h-full min-h-0 w-64 shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white p-3 md:flex">
        <div className="mb-3 flex items-center gap-2 px-1 text-sm font-semibold text-gray-700">
          <HardDrive className="h-4 w-4 text-[#0364b8]" /> My files
        </div>
        <button onClick={() => setParent("root")} className={`mb-1 flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-100 ${parent === "root" ? "bg-[#eef4fb] text-[#0364b8]" : "text-gray-700"}`}>
          <Home className="h-4 w-4" /> Home
        </button>
        {/* Windows-Explorer-style folder tree */}
        <div className="mt-1 flex-1 overflow-y-auto pr-1">
          <FolderTree
            folders={folders}
            currentId={parent}
            expanded={expandedTree}
            onToggle={(id) => {
              setExpandedTree((s) => {
                const n = new Set(s);
                if (n.has(id)) n.delete(id); else n.add(id);
                return n;
              });
            }}
            onOpen={(id) => setParent(id)}
          />
        </div>
        <div className="mt-2 border-t border-gray-100 pt-2">
          <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
            <HardDrive className="h-3.5 w-3.5" /> Storage
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div className={`h-full rounded-full ${lowDisk ? "bg-red-500" : "bg-[#0364b8]"}`} style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1 text-[11px] text-gray-400" data-testid="storage-usage-label">
            {usage
              ? `${fmtSize(usage.used_bytes)} used · ${fmtSize(usage.disk_free_bytes)} free of ${fmtSize(usage.disk_total_bytes)} on server`
              : "…"}
          </p>
          {lowDisk && (
            <p className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-red-500" data-testid="storage-low-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Low disk space — only {fmtSize(usage.disk_free_bytes)} left on the server
            </p>
          )}
        </div>
      </aside>

      {/* main (scrolls independently of the sidebar) */}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden" onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}>
        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-4 py-3">
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="file-upload-input" onChange={(e) => doUpload(e.target.files)} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn" className="flex items-center gap-2 rounded-md bg-[#0364b8] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0358a0] disabled:opacity-60">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
          </button>
          <button onClick={() => setNewFolderOpen(true)} data-testid="new-folder-btn" className="flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <FolderPlus className="h-4 w-4" /> New folder
          </button>
          <div className="relative ml-auto">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" data-testid="file-search" className="w-44 rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 focus:border-[#0364b8] focus:outline-none" />
          </div>
          <div className="flex overflow-hidden rounded-md border border-gray-300">
            <button onClick={() => setView("list")} className={`p-2 ${view === "list" ? "bg-[#eef4fb] text-[#0364b8]" : "text-gray-500"}`} aria-label="List view"><ListIcon className="h-4 w-4" /></button>
            <button onClick={() => setView("grid")} className={`p-2 ${view === "grid" ? "bg-[#eef4fb] text-[#0364b8]" : "text-gray-500"}`} aria-label="Grid view"><LayoutGrid className="h-4 w-4" /></button>
          </div>
        </div>

        {/* breadcrumb */}
        <div className="flex items-center gap-1 border-b border-gray-100 bg-white px-4 py-2 text-sm">
          <button onClick={() => setParent("root")} data-testid="crumb-root" className="font-medium text-gray-700 hover:text-[#0364b8]">My files</button>
          {breadcrumb.map((c) => (
            <span key={c.file_id} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
              <button onClick={() => setParent(c.file_id)} className="text-gray-700 hover:text-[#0364b8]">{c.name}</button>
            </span>
          ))}
        </div>

        {/* content */}
        <div ref={contentScrollRef} className={`relative min-h-0 flex-1 overflow-y-auto p-4 ${dragOver ? "ring-2 ring-inset ring-[#0364b8]" : ""}`}>
          {dragOver && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-[#0364b8]/5 text-sm font-semibold text-[#0364b8]">
              Drop files to upload
            </div>
          )}
          {loading ? (
            <div className="py-20 text-center text-sm text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center text-sm text-gray-400">
              This folder is empty. Drag files here or use Upload.
            </div>
          ) : view === "list" ? (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="grid grid-cols-[36px_1fr_40px] gap-3 border-b border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:grid-cols-[36px_1fr_120px_140px_40px]">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleAll(); }}
                  data-testid="select-all-btn"
                  aria-label={allSelected ? "Deselect all" : "Select all"}
                  className="flex h-5 w-5 items-center justify-center rounded border border-gray-300 bg-white hover:border-[#0364b8]"
                >
                  {allSelected ? <CheckSquare className="h-4 w-4 text-[#0364b8]" /> : someSelected ? <span className="block h-2 w-2 rounded-sm bg-[#0364b8]" /> : <Square className="h-4 w-4 text-transparent" />}
                </button>
                <span>Name</span><span className="hidden sm:block">Size</span><span className="hidden sm:block">Modified</span><span></span>
              </div>
              {filtered.map((item) => {
                const { Icon, cls } = iconFor(item);
                const checked = selected.has(item.file_id);
                return (
                  <div
                    key={item.file_id}
                    data-testid="file-row"
                    onClick={() => openItem(item)}
                    {...handlersFor(item)}
                    className={`grid cursor-pointer grid-cols-[36px_1fr_40px] items-center gap-3 border-b border-gray-100 px-4 py-3 hover:bg-[#f3f9ff] sm:grid-cols-[36px_1fr_120px_140px_40px] ${checked ? "bg-[#eef4fb]" : ""}`}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleOne(item.file_id); }}
                      data-testid={`select-file-${item.file_id}`}
                      aria-label={checked ? "Deselect" : "Select"}
                      className="flex h-5 w-5 items-center justify-center rounded border border-gray-300 bg-white hover:border-[#0364b8]"
                    >
                      {checked ? <CheckSquare className="h-4 w-4 text-[#0364b8]" /> : <Square className="h-4 w-4 text-transparent" />}
                    </button>
                    <span className="flex min-w-0 items-center gap-3 truncate">
                      <Icon className={`h-5 w-5 shrink-0 ${cls}`} />
                      <span className="truncate text-sm font-medium text-gray-800">{item.name}</span>
                    </span>
                    <span className="hidden text-sm text-gray-500 sm:block">{item.is_folder ? "—" : fmtSize(item.size)}</span>
                    <span className="hidden text-sm text-gray-500 sm:block">{fmtDate(item.updated_at)}</span>
                    <ItemActions item={item} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {filtered.map((item) => {
                const { Icon, cls } = iconFor(item);
                const checked = selected.has(item.file_id);
                return (
                  <div
                    key={item.file_id}
                    data-testid="file-card"
                    onClick={() => openItem(item)}
                    {...handlersFor(item)}
                    className={`group relative cursor-pointer rounded-lg border p-4 hover:border-[#0364b8] hover:shadow-sm ${checked ? "border-[#0364b8] bg-[#eef4fb]" : "border-gray-200 bg-white"}`}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleOne(item.file_id); }}
                      data-testid={`select-file-${item.file_id}`}
                      aria-label={checked ? "Deselect" : "Select"}
                      className={`absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded border border-gray-300 bg-white transition-opacity ${checked ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                    >
                      {checked ? <CheckSquare className="h-4 w-4 text-[#0364b8]" /> : <Square className="h-4 w-4 text-transparent" />}
                    </button>
                    <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100"><ItemActions item={item} /></div>
                    {item.is_folder || !(item.mime_type || "").startsWith("image/") ? (
                      <Icon className={`mb-3 h-10 w-10 ${cls}`} />
                    ) : (
                      <FileThumb fileId={item.file_id} fallback={<Icon className={`mb-3 h-10 w-10 ${cls}`} />} />
                    )}
                    <p className="truncate text-sm font-medium text-gray-800">{item.name}</p>
                    <p className="text-xs text-gray-400">{item.is_folder ? "Folder" : fmtSize(item.size)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* bulk action bar */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="pointer-events-none fixed inset-x-0 bottom-4 z-30 flex justify-center px-3 sm:bottom-6"
        >
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-full border border-gray-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
            <span className="rounded-full bg-[#eef4fb] px-2.5 py-1 font-mono text-[11px] font-semibold text-[#0364b8]" data-testid="bulk-count">
              {selected.size} selected
            </span>
            <button onClick={() => setPickerMode("move")} disabled={bulkBusy} data-testid="bulk-move-btn" className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-[#0364b8] hover:text-[#0364b8]">
              <MoveIcon className="h-3.5 w-3.5" /> Move to…
            </button>
            <button onClick={() => setPickerMode("copy")} disabled={bulkBusy || selectedFileIds.length === 0} data-testid="bulk-copy-btn" className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-[#0364b8] hover:text-[#0364b8] disabled:opacity-40" title={selectedFileIds.length === 0 ? "Folders can't be copied" : ""}>
              <CopyIcon className="h-3.5 w-3.5" /> Copy to…
            </button>
            <button onClick={bulkEmail} disabled={bulkBusy || selectedFileIds.length === 0} data-testid="bulk-email-btn" className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-[#0364b8] hover:text-[#0364b8] disabled:opacity-40" title={selectedFileIds.length === 0 ? "Folders can't be emailed" : ""}>
              <Mail className="h-3.5 w-3.5" /> Email
            </button>
            <button onClick={() => setConfirmDeleteOpen(true)} disabled={bulkBusy} data-testid="bulk-delete-btn" className="flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
            <button onClick={clearSelection} disabled={bulkBusy} data-testid="bulk-clear-btn" aria-label="Clear selection" className="rounded-full px-2 py-1 text-xs text-gray-400 hover:text-gray-700">
              ✕
            </button>
          </div>
        </div>
      )}

      <FolderPicker
        open={pickerMode !== null}
        onOpenChange={(o) => { if (!o) setPickerMode(null); }}
        title={pickerMode === "copy" ? "Copy to folder" : "Move to folder"}
        confirmLabel={pickerMode === "copy" ? "Copy here" : "Move here"}
        excludeIds={Array.from(selected)}
        onConfirm={(dest) => (pickerMode === "copy" ? bulkCopy(dest) : bulkMove(dest))}
      />

      {ctx && (
        <FileContextMenu ctx={ctx} onClose={() => setCtx(null)} browserOn={browserOn} onOpen={openItem} onDownload={download}
          onRename={(it) => { setRenameTarget(it); setRenameValue(it.name); }} onDelete={remove} onAddToBrowser={addToBrowser} />
      )}

      <Dialog open={!!confirmItem} onOpenChange={(o) => !o && setConfirmItem(null)}>
        <DialogContent className="bg-white text-gray-800 sm:max-w-sm" data-testid="file-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {confirmItem?.is_folder ? "folder" : "file"}?</DialogTitle>
            <DialogDescription className="text-gray-500">
              <span className="font-medium text-gray-800">{confirmItem?.name}</span> will be removed{confirmItem?.is_folder ? " with everything inside it" : ""}. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmItem(null)} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button onClick={doRemove} data-testid="file-delete-confirm" className="bg-red-600 text-white hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="bg-white text-gray-800 sm:max-w-sm" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {selected.size} item{selected.size === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription className="text-gray-500">
              This can&apos;t be undone. Folders are removed with everything inside them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)} disabled={bulkBusy} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button onClick={bulkDelete} disabled={bulkBusy} data-testid="bulk-delete-confirm" className="bg-red-600 text-white hover:bg-red-700">
              {bulkBusy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* new folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="bg-white text-gray-800 sm:max-w-sm" data-testid="new-folder-dialog">
          <DialogHeader>
            <DialogTitle>New folder</DialogTitle>
            <DialogDescription className="text-gray-500">Create a folder in the current location.</DialogDescription>
          </DialogHeader>
          <Input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} placeholder="Folder name" data-testid="new-folder-input" className="border-gray-300 text-gray-800" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button onClick={createFolder} data-testid="create-folder-btn" className="bg-[#0364b8] text-white hover:bg-[#0358a0]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="bg-white text-gray-800 sm:max-w-sm" data-testid="rename-dialog">
          <DialogHeader>
            <DialogTitle>Rename</DialogTitle>
            <DialogDescription className="text-gray-500">Enter a new name.</DialogDescription>
          </DialogHeader>
          <Input autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doRename()} data-testid="rename-input" className="border-gray-300 text-gray-800" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} className="border-gray-300 text-gray-700">Cancel</Button>
            <Button onClick={doRename} data-testid="rename-save-btn" className="bg-[#0364b8] text-white hover:bg-[#0358a0]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {previewFile && (
        <FilePreview file={previewFile} onClose={() => setPreviewFile(null)} onDownload={download} />
      )}

      <CopyProgressModal
        progress={copyProgress}
        onCancel={() => {
          copyCancelRef.current = true;
          setCopyProgress(null);
        }}
      />
    </div>
  );
};

export default FilesTab;
